import styled from "@emotion/styled";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { StepLiquidityPool } from "@stepfinance/liquidity";
import { BaseButton } from "@stepfinance/react";
import { Numberu64 } from "@stepfinance/step-swap";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import BN from "bn.js";
import type { FC } from "react";
import { useState } from "react";

import { useAccountsContextRefresher } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useMint } from "../../hooks";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { removeLiquidity } from "../../utils/pools";
import { handlePreSendTxnError } from "../../utils/transactions";
import { generateRemoveLabel } from "../labels";

export type RemoveLiquidityButtonProps = {
  pool: StepLiquidityPool;
  account: NormalizedTokenAccount | undefined;
  removeRatio: number;
};

export const NewRemoveLiquidityButton: FC<RemoveLiquidityButtonProps> = ({ pool, account, removeRatio }) => {
  const [pendingTx, setPendingTx] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const { connected } = useEnrichedWallet();
  const wallet = useConnectedWallet();
  const { connection } = useConnection();
  const refreshAccounts = useAccountsContextRefresher();
  const { refreshPools } = useLiquidity();
  const mintInfoA = useMint(pool.mints[0]);
  const mintInfoB = useMint(pool.mints[1]);
  const accountAmountBN = account ? new BN(account?.tokenAmount.amount) : new BN(0);

  const liquidityAmount = new Numberu64(accountAmountBN.muln(removeRatio).toString());
  const hasSufficientBalance = liquidityAmount.lte(accountAmountBN);

  const onRemove = async () => {
    try {
      if (!wallet || !account) {
        return;
      }

      setPendingTx(true);
      await removeLiquidity(
        connection,
        wallet,
        liquidityAmount,
        account,
        mintInfoA,
        mintInfoB,
        setPendingConfirmation,
        pool,
      );
    } catch (err) {
      captureException(err);
      handlePreSendTxnError(err as Error, "Error removing liquidity");
    } finally {
      setPendingTx(false);
      refreshAccounts();
      void refreshPools();
    }
  };

  return (
    <BaseRemoveLiquidityButton
      onClick={onRemove}
      disabled={connected && (pendingTx || !hasSufficientBalance || !account || !liquidityAmount)}
    >
      {generateRemoveLabel(
        pendingTx,
        pendingConfirmation,
        connected,
        liquidityAmount.toNumber(),
        pool,
        hasSufficientBalance,
      )}
    </BaseRemoveLiquidityButton>
  );
};

const BaseRemoveLiquidityButton = styled(BaseButton)`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  line-height: 22px;
  height: 60px;
  letter-spacing: 0em;

  span {
    text-align: center;
  }
`;
